import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CrosshairsTool, StackScrollTool, ZoomTool } from "@cornerstonejs/tools";
import { MOBILE_MORE_TOOLS, MPR_TOOL_OBJECT, USERTYPES } from "../../../../constants/Constants";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import themeContext from "../../../../context/theme/ThemeContext";
// NEW: role gate (same as desktop SCDICOMDetails) for the capture/save tiles.
import userContext from "../../../../context/users/UserContext";
// NEW: reuse the desktop Save-PR / Mark-KO buttons verbatim — they are
//      self-contained (own modal + save service). They're rendered hidden and
//      always-mounted at the menu root, and the grid tiles trigger their modals
//      programmatically. Copy & Secondary-Capture are triggered via window
//      events instead (their handlers live in Viewport.jsx).
import SaveAsPRButton from "../Tools/SaveAsPRButton";
import MarkAsKOButton from "../Tools/MarkAsKOButton";
import Tool from "../Tools/ToolComponents/Tool";
// OLD: import MPRTool from "../Tools/ToolComponents/MPRTool"; (its mobile tile
//      hardcodes a white 8px label that didn't match the other More tiles)
// NEW: use GetToolSVG for the Crosshairs icon and our shared labelStyle instead.
import GetToolSVG from "../Tools/ToolComponents/GetToolSVG";
import MobileMprMenu from "./MobileMprMenu";
import MobileSyncMenu from "./MobileSyncMenu";
import Overlay from "../../../../components/SVGComponents/Tools/Overlay";
import VerticalMore from "../../../../components/SVGComponents/Tools/VerticalMore";
import { toolStyle } from "../../styles/styles";
// Image Enhancement (mounted here, opens its own bottom sheet via
// event) and its icon; Spine Label uses GetToolSVG's "SpineLabelling" icon (already imported).
import ImageEnhancementTool from "../Tools/ToolComponents/ImageEnhancementTool";
import ImageEnhancementIcon from "../../../../components/SVGComponents/Tools/ImageEnhancementIcon";
import ColorLUTTool from "../Tools/ToolComponents/ColorLUTTool";
import ColorLutIcon from "../../../../components/SVGComponents/Tools/ColorLutIcon";

// Self-contained "More" button + anchored grid popup (opens UPWARD above the
// button, MedDream-style — not a bottom sheet). The grid holds the tools that
// don't live on the main bar; each reuses <Tool> for shared activation logic.
const MobileMoreMenu = () => {
  const { activeModality } = useContext(ThumbnailContext);
  // OLD: const { showOverlay, setShowOverlay, isMPRActive } = useContext(ViewportContext);
  // NEW: also pull the MPR-tool setters so the Crosshairs tile can activate via
  //      the MPR path (setActiveMPRTool) and reflect its active state.
  const {
    showOverlay,
    setShowOverlay,
    isMPRActive,
    activeMPRTool,
    setActiveMPRTool,
    setPreviousActiveMPRTool,
    // NEW: which viewport the capture/save events should target.
    activeViewportIndex,
    // NEW: Spine Label activation (same as desktop ToolDropDown).
    isSpineLabellingActive,
    setIsSpineLabellingActive,
    setSpineLabels,
    handleDefaultToolChange,
  } = useContext(ViewportContext);
  const { theme } = useContext(themeContext);
  // NEW: same visibility gate as desktop SCDICOMDetails — only radiologists /
  //      supervisors can copy / capture / save PR / mark KO.
  const { roles } = useContext(userContext);
  const canCapture =
    roles?.includes(USERTYPES.RADIOLOGIST) ||
    roles?.includes(USERTYPES.SUPERVISOR);
  const { t } = useTranslation();

  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ bottom: 0, right: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  // NEW: hosts for the always-mounted Save-PR / Mark-KO buttons (see note where
  //      they're rendered). The grid tiles click these programmatically.
  const prHostRef = useRef(null);
  const koHostRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const toggleMenu = () => {
    setShowMenu((prev) => {
      const next = !prev;
      if (next && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        // Anchor the popup above the button, right-aligned to it.
        setMenuPos({
          bottom: window.innerHeight - rect.top + 6,
          right: Math.max(8, window.innerWidth - rect.right),
        });
      }
      return next;
    });
  };

  const iconBoxStyle = {
    height: "65%",
    width: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const labelStyle = {
    fontSize: "9px",
    lineHeight: "100%",
    fontWeight: "400",
    color: "var(--mobile-color-txt)",
    display: "block",
    width: "60px",
    textAlign: "center",
    marginTop: "4px",
  };
  const cellStyle = { display: "flex", justifyContent: "center" };

  // NEW: the Copy / SC-Capture / Save-PR / Mark-KO icons are CSS
  // background-image SVGs with a baked-in light-gray fill (#e9e9e9) meant for
  // the DARK desktop viewport overlay. On the (light) mobile More menu they look
  // washed-out and don't match the themed icons (which use
  // theme.palette.inactiveTool). Since those icons are grayscale and
  // inactiveTool is a gray, recolor them to exactly that gray with a
  // brightness(0)+invert filter derived from the theme color — this adapts
  // automatically to light/dark theme. (No filter is applied on desktop; these
  // spans only get it here.)
  const grayHex = theme?.palette?.inactiveTool || "#424242";
  const grayLevel = (parseInt(grayHex.slice(1, 3), 16) || 66) / 255;
  const captureIconStyle = {
    filter: `brightness(0) saturate(100%) invert(${grayLevel})`,
  };

  // NEW: Copy & Secondary-Capture handlers live in Viewport.jsx (they read the
  // live canvas). We reach them by dispatching window events keyed on the active
  // viewport — Viewport.jsx listens for "aug:request-copy" and
  // "aug:request-sc-capture" and runs the matching handler.
  const requestCopyToClipboard = () => {
    console.warn("::MOBILE-CAPTURE:: [MobileMoreMenu] dispatch aug:request-copy", {
      activeViewportIndex,
    });
    window.dispatchEvent(
      new CustomEvent("aug:request-copy", {
        detail: { viewportIndex: activeViewportIndex },
      })
    );
    setShowMenu(false);
  };

  const requestScCapture = () => {
    console.warn(
      "::MOBILE-CAPTURE:: [MobileMoreMenu] dispatch aug:request-sc-capture",
      { activeViewportIndex }
    );
    window.dispatchEvent(
      new CustomEvent("aug:request-sc-capture", {
        detail: { viewportIndex: activeViewportIndex },
      })
    );
    setShowMenu(false);
  };

  // NEW: Save-PR / Mark-KO reuse the desktop button components, which own their
  // own modal state. Those components are rendered ALWAYS-MOUNTED at the root of
  // this menu (not inside the {showMenu} dropdown) — otherwise closing the menu
  // (which the click-outside handler does the moment you touch the portaled
  // modal) would unmount them and kill the open modal. The grid tiles just
  // programmatically click the real (hidden) button to open its dialog.
  const openPRDialog = () => {
    console.warn("::MOBILE-CAPTURE:: [MobileMoreMenu] open Save-PR dialog", {
      activeViewportIndex,
    });
    prHostRef.current?.querySelector(".pr-save-button")?.click();
    setShowMenu(false);
  };

  const openKODialog = () => {
    console.warn("::MOBILE-CAPTURE:: [MobileMoreMenu] open Mark-KO dialog", {
      activeViewportIndex,
    });
    koHostRef.current?.querySelector(".ko-mark-button")?.click();
    setShowMenu(false);
  };

  return (
    <div
      ref={menuRef}
      style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      {/* NEW: always-mounted PR/KO buttons (visually hidden). They carry their
          own Save-Presentation-State / Mark-as-Key-Object modals. Kept outside
          the {showMenu} dropdown so the menu closing (on modal interaction)
          never unmounts an open modal. Only mounted for capture-capable roles. */}
      {canCapture && (
        <>
          <span ref={prHostRef} style={{ display: "none" }}>
            <SaveAsPRButton viewportIndex={activeViewportIndex} />
          </span>
          <span ref={koHostRef} style={{ display: "none" }}>
            <MarkAsKOButton viewportIndex={activeViewportIndex} />
          </span>
        </>
      )}

      {/*  always-mounted Image Enhancement tool. On mobile it renders no
          toolbar icon — it only listens for the `toggleImageEnhancement` event and shows its
          bottom sheet. The grid tile below dispatches that event. */}
      <ImageEnhancementTool />

      {/* Fusion (ColorLUTTool) — mounted so it can receive the `toggleFusion` event and render
          its series-select modal + fusion overlay. Renders nothing until opened. */}
      <ColorLUTTool />

      <div ref={buttonRef} onClick={toggleMenu} style={{ ...toolStyle }}>
        <div style={iconBoxStyle}>
          <div className="highlight-tool-mobile">
            <VerticalMore
              color={showMenu ? theme.palette.activeTool : theme.palette.inactiveTool}
            />
          </div>
        </div>
        <span style={labelStyle}>{t("viewer.tools.more")}</span>
      </div>

      {showMenu && (
        <div
          className="viewer-tool-more-dropdown"
          style={{
            position: "fixed",
            bottom: menuPos.bottom,
            right: menuPos.right,
            zIndex: 9999,
            borderRadius: "8px",
            padding: "14px 12px",
            maxHeight: "65vh",
            overflowY: "auto",
            boxShadow:
              "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "14px",
            }}
          >
            {MOBILE_MORE_TOOLS.map((tool, index) => {
              if (tool.NotFor.includes(activeModality)) {
                return null;
              }
              // Synchronize opens its own sub-menu (sync modes) — render
              // MobileSyncMenu instead of the generic <Tool> (which only toggled
              // automatic sync), and keep the grid open when its tile is tapped.
              if (tool.key === "Synchronize") {
                return (
                  <div key={index} style={cellStyle}>
                    <MobileSyncMenu onAction={() => setShowMenu(false)} />
                  </div>
                );
              }
              // Zoom & Stack Scroll open their OWN sub-dropdowns; don't close
              // the More grid when interacting with them (otherwise the caret
              // tap bubbles here and shuts the grid).
              const keepsMenuOpen =
                tool.key === ZoomTool.toolName ||
                tool.key === StackScrollTool.toolName;
              return (
                <div
                  key={index}
                  style={cellStyle}
                  onClick={keepsMenuOpen ? undefined : () => setShowMenu(false)}
                >
                  <Tool tool={tool} />
                </div>
              );
            })}

            {/*  Image Enhancement — opens the mobile bottom sheet. The
                always-mounted <ImageEnhancementTool /> listens for this event and renders it.
                Shown for all modalities, matching desktop. */}
            <div style={cellStyle}>
              <div
                className="cursor-pointer"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}
                onClick={() => {
                  document.dispatchEvent(new CustomEvent("toggleImageEnhancement"));
                  setShowMenu(false);
                }}
              >
                <div style={iconBoxStyle}>
                  <ImageEnhancementIcon color={theme.palette.inactiveTool} />
                </div>
                <span style={labelStyle}>{t("viewer.tools.image-enhancement", "Image Enhancement")}</span>
              </div>
            </div>

            {/*  Spine Label — activates spine labelling; SpineLabellingTool
                (mounted per-viewport in Viewport.jsx) then shows its own select modal + overlay.
                Same as desktop (all modalities). */}
            <div style={cellStyle}>
              <div
                className="cursor-pointer"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}
                onClick={() => {
                  setSpineLabels([]);
                  setIsSpineLabellingActive(true);
                  setShowMenu(false);
                }}
              >
                <div style={iconBoxStyle}>
                  <GetToolSVG toolName="SpineLabelling" hovered={isSpineLabellingActive} />
                </div>
                <span style={labelStyle}>{t("viewer.tools.spine-labelling")}</span>
              </div>
            </div>

            {/*  Fusion — opens the series-select modal (ColorLUTTool
                listens for this event). Same modality gate as desktop (CT / MR / PT),
                hidden in MPR mode like the desktop toolbar. */}
            {!isMPRActive && ["CT", "MR", "PT"].includes(activeModality) && (
              <div style={cellStyle}>
                <div
                  className="cursor-pointer"
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}
                  onClick={() => {
                    document.dispatchEvent(new CustomEvent("toggleFusion"));
                    setShowMenu(false);
                  }}
                >
                  <div style={iconBoxStyle}>
                    <ColorLutIcon color={theme.palette.inactiveTool} />
                  </div>
                  <span style={labelStyle}>{t("viewer.tools.fusion", "Fusion")}</span>
                </div>
              </div>
            )}

            {/* Crosshairs — MPR only. Measurement tools already live in the
                Measure button, so we don't duplicate them here; we just add
                Crosshairs.
                OLD: rendered via <MPRTool> whose mobile label was a hardcoded
                     white 8px span (didn't match the other tiles).
                NEW: custom tile using GetToolSVG + the shared labelStyle so it
                     looks identical to the other More tiles, and it activates
                     through the MPR path (setActiveMPRTool). */}
            {isMPRActive && (
              <div style={cellStyle}>
                <div
                  className="cursor-pointer"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => {
                    // toggle — if Crosshairs is already the active MPR
                    // tool, deselect it (same as desktop MPRTool.changeTool). OLD: always activated.
                    setPreviousActiveMPRTool(activeMPRTool);
                    if (activeMPRTool === CrosshairsTool.toolName) {
                      handleDefaultToolChange();
                    } else {
                      setActiveMPRTool(CrosshairsTool.toolName);
                    }
                    setShowMenu(false);
                  }}
                >
                  <div style={iconBoxStyle}>
                    <GetToolSVG
                      toolName={CrosshairsTool.toolName}
                      hovered={activeMPRTool === CrosshairsTool.toolName}
                    />
                  </div>
                  <span style={labelStyle}>{t("viewer.tools.crosshairs")}</span>
                </div>
              </div>
            )}

            {/* MPR — its own plane dropdown.
                OLD: <MobileMprMenu /> (parent More grid stayed open after a plane
                     was picked / MPR toggled).
                NEW: pass onAction to close this More grid once MPR is
                     enabled/switched/closed.
                MobileMprMenu returns null for X-ray studies
                     (MPR_TOOL_OBJECT.NotFor), but the wrapping grid cell still
                     occupied a slot -> visible empty gap. Gate the whole cell
                     with the same modality condition. */}
            {!MPR_TOOL_OBJECT.NotFor.includes(activeModality) && (
              <div style={cellStyle}>
                <MobileMprMenu onAction={() => setShowMenu(false)} />
              </div>
            )}

            {/* Overlay toggle */}
            <div style={cellStyle}>
              <div
                className="cursor-pointer"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => {
                  setShowOverlay(!showOverlay);
                  setShowMenu(false);
                }}
              >
                <div style={{ height: "24px", ...iconBoxStyle }}>
                  <Overlay
                    color={
                      showOverlay
                        ? theme.palette.activeTool
                        : theme.palette.inactiveTool
                    }
                  />
                </div>
                <span style={labelStyle}>{t("viewer.overlay.overlay")}</span>
              </div>
            </div>

            {/* NEW: viewport capture/save actions — the 4 buttons that sit in
                the viewport top-right on desktop (SCDICOMDetails). On mobile
                they live here in the More grid. Same role gate as desktop
                (RADIOLOGIST / SUPERVISOR). Copy & Secondary-Capture dispatch
                window events to Viewport.jsx; Save-PR / Mark-KO reuse the
                self-contained desktop components (they open their own modals). */}
            {canCapture && (
              <>
                {/* Copy viewport content to clipboard */}
                <div style={cellStyle}>
                  <div
                    className="cursor-pointer"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={requestCopyToClipboard}
                  >
                    <div style={iconBoxStyle}>
                      {/* NEW: captureIconStyle recolors the light-gray icon to
                          the theme's inactiveTool so it matches the other tiles. */}
                      <span
                        className="svg-icon-copy-to-clipboard"
                        style={captureIconStyle}
                      />
                    </div>
                    <span style={labelStyle}>{t("viewer.tools.copy")}</span>
                  </div>
                </div>

                {/* Save viewport content as Secondary Capture DICOM */}
                <div style={cellStyle}>
                  <div
                    className="cursor-pointer"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={requestScCapture}
                  >
                    <div style={iconBoxStyle}>
                      <span
                        className="secondary-capture-button"
                        style={captureIconStyle}
                      />
                    </div>
                    <span style={labelStyle}>{t("viewer.tools.capture")}</span>
                  </div>
                </div>

                {/* Save viewport content as PR — opens the Save Presentation
                    State modal via the always-mounted SaveAsPRButton (see the
                    hidden host at the menu root). */}
                <div style={cellStyle}>
                  <div
                    className="cursor-pointer"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={openPRDialog}
                  >
                    <div style={iconBoxStyle}>
                      <span className="pr-save-button" style={captureIconStyle} />
                    </div>
                    <span style={labelStyle}>{t("viewer.tools.save-pr")}</span>
                  </div>
                </div>

                {/* Mark this slice as a Key Object — opens the Mark as Key
                    Object modal via the always-mounted MarkAsKOButton. */}
                <div style={cellStyle}>
                  <div
                    className="cursor-pointer"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={openKODialog}
                  >
                    <div style={iconBoxStyle}>
                      <span className="ko-mark-button" style={captureIconStyle} />
                    </div>
                    <span style={labelStyle}>{t("viewer.tools.mark-ko")}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileMoreMenu;
